import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../api/axios";
import { getUserList, updateUserGrants } from "../../api/users/grant"; // 사용자 지정 경로 유지
import BoardTable from "../../components/common/board/BoardTable"; // 사용자 지정 경로 유지

export default function UserGrantList() {
  // 실제 테이블에 표시되는 데이터
  const [rows, setRows] = useState([]);
  // API에서 불러온 원본 데이터 (변경 사항 비교 및 롤백용)
  const [originalRows, setOriginalRows] = useState([]);
  // 저장되지 않은 변경 사항만 추적하는 객체 { userId: { is_staff: true, is_active: false, ... } }
  const [pendingChanges, setPendingChanges] = useState({});
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate(); // useNavigate는 사용하지 않지만 임포트 유지
  const [searchFilters, setSearchFilters] = useState({
    name: "",
    email: "",
    is_staff: "all", // all | true | false
    is_active: "all", // all | true | false
  });

  // ======================= API Callbacks ===========================

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      // /grant/list/ API 호출
      const res = await getUserList();
      const list = res.data?.results ?? res.data ?? [];

      // 원본 데이터와 표시되는 데이터를 모두 설정
      setOriginalRows(list);
      setRows(list);
      setPendingChanges({}); // 목록 로드 시 미저장 변경 사항 초기화
    } catch (e) {
      console.error("사용자 목록 로드 실패", e);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * 토글 버튼 클릭 시 호출됩니다. API 호출 없이 로컬 상태와 pendingChanges만 업데이트합니다.
   */
  const handleGrantToggle = useCallback(
    (userId, key, value) => {
      // 1. UI 상태 즉시 업데이트 (로컬 반영)
      setRows((prevRows) =>
        prevRows.map((u) => (u.id === userId ? { ...u, [key]: value } : u))
      );

      // 2. pendingChanges에 변경 사항 기록
      setPendingChanges((prev) => {
        const currentChanges = prev[userId] || {};
        const newChanges = { ...currentChanges, [key]: value };

        const originalRow = originalRows.find((r) => r.id === userId);

        // 만약 원래 값으로 돌아갔다면, pendingChanges에서 해당 키 제거
        if (originalRow && originalRow[key] === value) {
          delete newChanges[key];
        }

        // 해당 유저의 모든 변경 사항이 사라지면 유저 자체를 pendingChanges에서 제거
        if (Object.keys(newChanges).length === 0) {
          const { [userId]: removed, ...rest } = prev;
          return rest;
        }

        // 변경 사항이 있다면 업데이트
        return { ...prev, [userId]: newChanges };
      });
    },
    [originalRows]
  );

  /**
   * "변경 저장" 버튼 클릭 시 모든 pendingChanges를 API로 전송합니다.
   */
  const handleSave = useCallback(async () => {
    if (Object.keys(pendingChanges).length === 0) return;

    setLoading(true);
    const savePromises = [];

    // pendingChanges 순회하며 API 호출
    for (const userIdString in pendingChanges) {
      // ID는 숫자일 수 있으므로 파싱
      const userId = parseInt(userIdString);
      const data = pendingChanges[userIdString];
      // API 호출 Promise 저장
      savePromises.push(updateUserGrants(userId, data));
    }

    try {
      // 모든 API 호출이 완료될 때까지 기다림 (하나라도 실패하면 catch 블록으로 이동)
      await Promise.all(savePromises);

      alert("모든 권한 변경 사항이 성공적으로 저장되었습니다.");

      // 원본 데이터를 현재 rows 상태로 업데이트하고 pendingChanges 초기화
      setOriginalRows(rows);
      setPendingChanges({});
    } catch (e) {
      console.error("일부 권한 저장 실패:", e);
      alert(
        `일부 권한 수정에 실패했습니다: ${
          e.message || e
        }. 데이터를 다시 불러옵니다.`
      );

      // 실패 시 목록을 다시 로드하여 롤백 (API 결과에 맞춰 재동기화)
      loadUsers();
    } finally {
      setLoading(false);
    }
  }, [pendingChanges, loadUsers, rows]);

  // 컴포넌트 로드 시 사용자 목록 로드
  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  //검색
  const handleFilterChange = (key, value) => {
    setSearchFilters((prev) => ({ ...prev, [key]: value }));
  };

  useEffect(() => {
    let filtered = originalRows.filter((user) => {
      const nameMatch = user.name
        ?.toLowerCase()
        .includes(searchFilters.name.toLowerCase());
      const emailMatch = user.email
        ?.toLowerCase()
        .includes(searchFilters.email.toLowerCase());
      const staffMatch =
        searchFilters.is_staff === "all" ||
        user.is_staff === (searchFilters.is_staff === "true");
      const activeMatch =
        searchFilters.is_active === "all" ||
        user.is_active === (searchFilters.is_active === "true");

      return nameMatch && emailMatch && staffMatch && activeMatch;
    });

    setRows(filtered);
  }, [searchFilters, originalRows]);
  // ======================= Columns (BoardTable 구성 참고) ===========================
  const columns = [
    {
      key: "name",
      header: "이름",
      // width: 150,
    },
    { key: "email", header: "이메일" },
    {
      key: "date_joined",
      header: "가입 날짜",
      render: (row) => {
        // row.date_joined가 'YYYY-MM-DDTHH:MM:SSZ' 형식의 문자열이라고 가정합니다.
        const dateString = row.date_joined;

        if (!dateString) return "-";

        try {
          // 1. Date 객체로 변환
          const date = new Date(dateString);

          // 2. YYYY-MM-DD 형식으로 포맷팅
          const year = date.getFullYear();
          // getMonth()는 0부터 시작하므로 +1, padStart로 두 자리 확보
          const month = String(date.getMonth() + 1).padStart(2, "0");
          const day = String(date.getDate()).padStart(2, "0");

          return `${year}-${month}-${day}`;
        } catch (e) {
          // 유효하지 않은 날짜 형식일 경우
          return dateString.split("T")[0] || "-";
        }
      },
    },
    {
      key: "is_staff",
      header: "관리자 권한",
      // width: 120,
      // BoardTable의 render 속성 내부에서 토글 버튼 구현
      render: (row) => (
        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            // checked는 rows 상태를 따름 (로컬 변경 반영)
            checked={row.is_staff}
            // 토글 시 handleGrantToggle 호출 (API 호출은 여기서 발생하지 않음)
            onChange={(e) =>
              handleGrantToggle(row.id, "is_staff", e.target.checked)
            }
            className="sr-only peer"
          />
          {/* Tailwind CSS를 사용한 토글 스타일 */}
          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
        </label>
      ),
    },
    {
      key: "is_active",
      header: "계정 활성화",
      // width: 120,
      render: (row) => (
        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={row.is_active}
            onChange={(e) =>
              handleGrantToggle(row.id, "is_active", e.target.checked)
            }
            className="sr-only peer"
          />
          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
        </label>
      ),
    },
  ];

  // ======================= Render ===========================
  return (
    <div className="p-6">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-xl font-semibold">사용자 권한 관리</h1>

        {/* 변경 저장 버튼 - pendingChanges가 있을 때만 활성화 */}
        <div className="flex gap-2">
          <input
            type="text"
            placeholder="이름 검색"
            value={searchFilters.name}
            onChange={(e) => handleFilterChange("name", e.target.value)}
            className="px-2 py-1 border rounded"
          />
          <input
            type="text"
            placeholder="이메일 검색"
            value={searchFilters.email}
            onChange={(e) => handleFilterChange("email", e.target.value)}
            className="px-2 py-1 border rounded"
          />

          <select
            value={searchFilters.is_staff}
            onChange={(e) => handleFilterChange("is_staff", e.target.value)}
            className="px-2 py-1 border rounded"
          >
            <option value="all">관리자 전체</option>
            <option value="true">관리자만</option>
            <option value="false">일반 사용자</option>
          </select>

          <select
            value={searchFilters.is_active}
            onChange={(e) => handleFilterChange("is_active", e.target.value)}
            className="px-2 py-1 border rounded"
          >
            <option value="all">활성 전체</option>
            <option value="true">활성 계정</option>
            <option value="false">비활성 계정</option>
          </select>
        </div>

        {/* 변경 저장 버튼 */}
        <button
          className={`px-4 py-2 text-white rounded-lg shadow-md ${
            Object.keys(pendingChanges).length > 0 && !loading
              ? "bg-indigo-600 hover:bg-indigo-700"
              : "bg-gray-400 cursor-not-allowed"
          }`}
          onClick={handleSave}
          disabled={Object.keys(pendingChanges).length === 0 || loading}
        >
          {loading
            ? "저장 중..."
            : `변경 저장 (${Object.keys(pendingChanges).length})`}
        </button>
      </div>

      <BoardTable
        columns={columns}
        rows={rows}
        loading={loading}
        keyField="id"
        emptyText="등록된 사용자가 없습니다."
      />
    </div>
  );
}
